import { useEffect, useRef, useState, useCallback } from 'react'

export function useHeartbeatSound(bpm: number | null, isConnected: boolean) {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('aurora_sound_enabled') === 'true'
  })

  const audioCtxRef = useRef<AudioContext | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const playBeat = useCallback(() => {
    const ctx = audioCtxRef.current
    if (!ctx || ctx.state === 'closed') return

    const now = ctx.currentTime

    // "Lub" tone — 80Hz, 60ms, gain 0.4
    const lubOsc = ctx.createOscillator()
    const lubGain = ctx.createGain()
    lubOsc.type = 'sine'
    lubOsc.frequency.value = 80
    lubGain.gain.setValueAtTime(0, now)
    lubGain.gain.linearRampToValueAtTime(0.4, now + 0.01)
    lubGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06)
    lubOsc.connect(lubGain)
    lubGain.connect(ctx.destination)
    lubOsc.start(now)
    lubOsc.stop(now + 0.07)

    // "Dub" tone — 60Hz, 40ms, gain 0.3, 120ms after lub
    const dubOsc = ctx.createOscillator()
    const dubGain = ctx.createGain()
    dubOsc.type = 'sine'
    dubOsc.frequency.value = 60
    dubGain.gain.setValueAtTime(0, now + 0.12)
    dubGain.gain.linearRampToValueAtTime(0.3, now + 0.13)
    dubGain.gain.exponentialRampToValueAtTime(0.001, now + 0.17)
    dubOsc.connect(dubGain)
    dubGain.connect(ctx.destination)
    dubOsc.start(now + 0.12)
    dubOsc.stop(now + 0.18)
  }, [])

  const stopSound = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Start/stop sound based on state
  useEffect(() => {
    if (!soundEnabled || !isConnected || !bpm || bpm <= 0) {
      stopSound()
      return
    }

    // Create AudioContext on demand (must be after user gesture)
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext()
    }

    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume()
    }

    const intervalMs = 60000 / bpm
    stopSound()
    playBeat() // Play immediately
    intervalRef.current = setInterval(playBeat, intervalMs)

    return () => {
      stopSound()
    }
  }, [soundEnabled, isConnected, bpm, playBeat, stopSound])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSound()
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close()
      }
    }
  }, [stopSound])

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev
      localStorage.setItem('aurora_sound_enabled', String(next))
      // Create AudioContext on user gesture if needed
      if (
        next &&
        (!audioCtxRef.current || audioCtxRef.current.state === 'closed')
      ) {
        audioCtxRef.current = new AudioContext()
      }
      return next
    })
  }, [])

  return { soundEnabled, toggleSound }
}
